import React, { useEffect, useState, useRef, FC } from "react";
import { makeStyles, useTheme } from "@seahorse/ui";
import { TextField, Button } from "@seahorse/ui";
import {
  coTraderQuery,
  getAuthenticatedWebSocketUrl,
  giveFeedback,
  useFxSuggestedQuestions,
} from "../Utils/CoTraderUtils";
import {
  defaultMessageByLanguage,
  enterMessageByLanguage,
  fxgptChatSuggestionsByLanguage,
} from "../Utils/ChatSuggestions";
import { useResponsiveTileTypography } from "../Utils/useResponsiveTileTypography";
import { domToReact, htmlToDOM } from "html-react-parser";
import CollapsibleDiv from "../../UtilComponent/CollapsibleDiv";
import LinkPreview from "../../UtilComponent/LinkPreview";
import HighchartFromText from "../../UtilComponent/HighchartFromText";
import ChatFinePrint from "../../../Components/Common/ChatFinePrint";
import Switch from "react-switch";
import { FaCopy as _FaCopy, FaDownload as _FaDownload, FaPen as _FaPen, FaRedo as _FaRedo, FaThumbsDown as _FaThumbsDown, FaThumbsUp as _FaThumbsUp } from "react-icons/fa";

import html2pdf from "html2pdf.js";
const FaCopy = _FaCopy as unknown as FC<any>;
const FaDownload = _FaDownload as unknown as FC<any>;
const FaPen = _FaPen as unknown as FC<any>;
const FaRedo = _FaRedo as unknown as FC<any>;
const FaThumbsUp = _FaThumbsUp as unknown as FC<any>;
const FaThumbsDown = _FaThumbsDown as unknown as FC<any>;


interface CoTraderChatComponentProps {
  currencyPair: string;
  variant?: "default" | "preTrade";
  normalizeSummaryUtcNoZone?: boolean;
  normalizeBestXLabels?: boolean;
}

type ChatMessage = {
  type: string;
  text: string;
  message_id?: any;
  sources?: string[];
};

// Opens an analysis block the moment a question is sent; the socket's own INFO
// steps stream in underneath it and the block is closed as usual.
const PROCESSING_PLACEHOLDER = "<CollapsibleDiv>Processing\n";

const SUGGESTION_CONTEXT_LIMIT = 4;
const SUGGESTION_CONTEXT_NOTICE =
  "For the most accurate answers, reset the chat to clear old context before continuing.";

const parseSummaryPayload = (message: any) => {
  if (message == null) return null;

  if (typeof message === "string") {
    try {
      return JSON.parse(message);
    } catch {
      return null;
    }
  }

  return message;
};

const SUMMARY_TIMESTAMP_RE =
  /\b\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?: ?(?:Z|UTC|GMT|[+-]\d{2}:?\d{2}))\b/gi;

const formatUtcTimestampWithoutZone = (timestampMs: number) => {
  const date = new Date(timestampMs);
  const pad = (value: number, size = 2) => String(value).padStart(size, "0");

  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(
    date.getUTCDate()
  )}T${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(
    date.getUTCSeconds()
  )}`;
};

const normalizeSummaryTimestampMatch = (value: string) => {
  const normalizedValue = value
    .trim()
    .replace(/^(\d{4}-\d{2}-\d{2}) /, "$1T")
    .replace(/\s+(UTC|GMT)$/i, "Z")
    .replace(/([+-]\d{2})(\d{2})$/, "$1:$2");

  const timestampMs = new Date(normalizedValue).getTime();
  if (!Number.isFinite(timestampMs)) {
    return value;
  }

  return formatUtcTimestampWithoutZone(timestampMs);
};

const normalizeSummaryTextToUtcNoZone = (value: string) =>
  value.replace(SUMMARY_TIMESTAMP_RE, normalizeSummaryTimestampMatch);

const normalizeBestXLabelsInText = (value: string) =>
  value
    .replace(/\bBestX\s+Market\s+Summary\b/gi, "Market Summary")
    .replace(/\bBestX\s+Chat\b/gi, "Chat");

const CoTraderChatTileComponent: React.FunctionComponent<
  CoTraderChatComponentProps
> = ({
  currencyPair,
  variant = "default",
  normalizeSummaryUtcNoZone = false,
  normalizeBestXLabels = false,
}) => {
    const theme = useTheme();
    const isPreTrade = variant === "preTrade";
    const { containerRef, tier } =
      useResponsiveTileTypography<HTMLDivElement>();
    const colors = {
      Pending: theme.globallink.colors.DataTurquoise[500],
      Ineligible: theme.globallink.colors.DataOrange[500],
      Cleared: theme.globallink.colors.DataPurple[500],
      Rejected: theme.globallink.colors.DataAmber[600],
      Dark: theme.globallink.colors.Grey[1100],
      Light: theme.globallink.colors.White[500],
      question: theme.globallink.colors.Blue[500],
      answer: theme.globallink.colors.White[500],
      reportText: "#E8EDF4",
      reportBorder: "rgba(255,255,255,0.08)",
    };
    const userLanguage = localStorage.getItem("user_language") || "English";
    const {
      suggestedQuestions,
      suggestedQuestionsMeta,
      suggestedQuestionsLoading,
      suggestedQuestionsError,
    } = useFxSuggestedQuestions({
      instrument: currencyPair,
      language: userLanguage,
      enabled: true,
    });
    const chatSuggestions = React.useMemo<string[]>(() => {
      const useApiSuggestions =
        !suggestedQuestionsError &&
        (suggestedQuestionsLoading || suggestedQuestionsMeta);
      const suggestions: string[] = useApiSuggestions
        ? suggestedQuestions
        : fxgptChatSuggestionsByLanguage[userLanguage] ||
        fxgptChatSuggestionsByLanguage["English"];

      return suggestions.map((suggestion: string) =>
        suggestion.replace("{symbol}", currencyPair)
      );
    }, [
      currencyPair,
      suggestedQuestions,
      suggestedQuestionsError,
      suggestedQuestionsLoading,
      suggestedQuestionsMeta,
      userLanguage,
    ]);
    const defaultMessage =
      defaultMessageByLanguage[userLanguage] ||
      defaultMessageByLanguage["English"];
    const enterMessage =
      enterMessageByLanguage[userLanguage] || enterMessageByLanguage["English"];

    const typography = React.useMemo(() => {
      switch (tier) {
        case "compact":
          return {
            containerPadding: "8px",
            bodyPadding: "8px",
            messagePadding: "7px 10px",
            messageSize: "12px",
            messageLineHeight: 1.45,
            headerSize: "13px",
            bodyGap: "8px",
            noticeSize: "10px",
            noticeLineHeight: "14px",
            suggestionSize: "11px",
            suggestionHeight: "3.1em",
            finePrintSize: "9px",
            finePrintLineHeight: "13px",
          };
        case "comfortable":
          return {
            containerPadding: "12px",
            bodyPadding: "12px",
            messagePadding: "10px 14px",
            messageSize: "14px",
            messageLineHeight: 1.6,
            headerSize: "15px",
            bodyGap: "12px",
            noticeSize: "12px",
            noticeLineHeight: "17px",
            suggestionSize: "13px",
            suggestionHeight: "3.5em",
            finePrintSize: "10.5px",
            finePrintLineHeight: "15px",
          };
        default:
          return {
            containerPadding: "10px",
            bodyPadding: "10px",
            messagePadding: "8px 12px",
            messageSize: "13px",
            messageLineHeight: 1.5,
            headerSize: "14px",
            bodyGap: "10px",
            noticeSize: "11px",
            noticeLineHeight: "16px",
            suggestionSize: "12px",
            suggestionHeight: "3.3em",
            finePrintSize: "10px",
            finePrintLineHeight: "14px",
          };
      }
    }, [tier]);

    const useStyles = makeStyles({
      container: {
        width: "100%",
        height: "100%",
        display: "flex",
        marginTop: "10px",
        marginBottom: "10px",
        flexDirection: "column",
        borderRadius: "9px",
        padding: "10px",
        border: "1px solid rgba(255,255,255,0.14)",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",
      },
      preTradeContainer: {
        boxSizing: "border-box",
        borderRadius: "12px",
        margin: 0,
        minHeight: 0,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.14)",
        background:
          "linear-gradient(180deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",
      },
      container1: {
        overflow: "auto",
        height: "95%",
        width: "100%",
        backgroundColor: "transparent",
        padding: "10px",
        display: "flex",
        flexDirection: "column",
        borderRadius: "8px",
      },
      preTradeBody: {
        boxSizing: "border-box",
        flex: 1,
        height: "auto",
        minHeight: 0,
        overflow: "auto",
        backgroundColor: "transparent",
        gap: "10px",
      },
      message: {
        margin: "10px 0",
        padding: "8px",
        borderRadius: "12px",
        color: colors.reportText,
        width: "fit-content",
      },
      question: {
        backgroundColor: colors.question, // Light purple for questions
        alignSelf: "flex-end",
        marginLeft: "auto",
        maxWidth: "80%",
        color: "white",
      },
      // Answers read as plain text on the tile; only questions get a bubble.
      answer: {
        alignSelf: "flex-start",
        marginRight: "auto",
        maxWidth: "100%",
        width: "100%",
        color: colors.reportText,
        "& code": {
          background: "rgba(255,255,255,0.08)",
          color: colors.reportText,
        },
      },
      actionIcon: {
        cursor: "pointer",
        fontSize: "12px",
        color: "rgba(232,237,244,0.55)",
        opacity: 1,
        transition: "color 0.15s ease",
        "&:hover": {
          color: colors.reportText,
        },
      },
      actionRow: {
        display: "flex",
        alignItems: "center",
        gap: "14px",
        marginTop: "8px",
        transition: "opacity 120ms ease",
      },
      feedbackBox: {
        width: "100%",
        marginTop: "10px",
        padding: "10px",
        borderRadius: "8px",
        backgroundColor: "rgba(255,255,255,0.04)",
        border: `1px solid ${colors.reportBorder}`,
      },
      feedbackLabel: {
        display: "block",
        marginBottom: "6px",
        fontSize: "12px",
        fontWeight: 600,
        color: colors.reportText,
      },
      feedbackInput: {
        display: "block",
        boxSizing: "border-box",
        width: "100%",
        minHeight: "56px",
        padding: "8px 10px",
        resize: "vertical",
        fontFamily: "inherit",
        fontSize: "inherit",
        lineHeight: "inherit",
        color: colors.reportText,
        backgroundColor: "rgba(0,0,0,0.18)",
        border: "1px solid rgba(255,255,255,0.14)",
        borderRadius: "6px",
        outline: "none",
        transition: "border-color 0.15s ease",
        "&::placeholder": {
          color: "rgba(232,237,244,0.45)",
        },
        "&:focus": {
          borderColor: theme.globallink.colors.Blue[500],
        },
      },
      feedbackActions: {
        display: "flex",
        justifyContent: "flex-end",
        gap: "8px",
        marginTop: "8px",
      },
      feedbackButton: {
        minWidth: 0,
        padding: "4px 12px",
        fontSize: "12px",
        textTransform: "none",
      },
      feedbackCancelButton: {
        color: "rgba(224,224,224,0.76)",
        "&:hover": {
          backgroundColor: "rgba(255,255,255,0.08)",
          color: colors.Light,
        },
      },
      chatHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "10px",
        marginBottom: "8px",
        color: colors.Light,
      },
      // "Ask EMAlpha" sits level with "Insights" / "Algo vs RFQ".
      preTradeHeader: {
        boxSizing: "border-box",
        minHeight: "46px",
        padding: "0 6px",
        marginBottom: 0,
        flexShrink: 0,
      },
      chatTitle: {
        fontSize: "14px",
        fontWeight: 700,
        lineHeight: "18px",
      },
      contextNotice: {
        width: "100%",
        margin: "8px 0 4px",
        padding: "8px 10px",
        borderRadius: "6px",
        backgroundColor: "rgba(255, 255, 255, 0.08)",
        border: "1px solid rgba(255, 255, 255, 0.12)",
        color: colors.Light,
        fontSize: "11px",
        lineHeight: "16px",
        textAlign: "center",
      },
      preTradeSuggestionGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gridAutoRows: "1fr",
        gap: "8px",
        "@media (max-width: 720px)": {
          gridTemplateColumns: "minmax(0, 1fr)",
        },
      },
      preTradeSuggestionButton: {
        width: "100%",
        minHeight: "56px",
        justifyContent: "flex-start",
        textAlign: "left",
        textTransform: "none",
        whiteSpace: "normal",
        lineHeight: 1.35,
        padding: "12px 14px",
        border: "1px solid rgba(255,255,255,0.14)",
        borderRadius: "14px",
        backgroundColor: "rgba(255,255,255,0.06)",
        color: colors.Light,
        boxShadow: "none",
        transition: "background-color 0.15s ease, border-color 0.15s ease",
        "&:hover": {
          backgroundColor: "rgba(255,255,255,0.1)",
          borderColor: "rgba(255,255,255,0.22)",
          transform: "none",
        },
      },
      preTradeComposer: {
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "10px",
        marginTop: "10px",
      },
      preTradeInputWrap: {
        flex: 1,
        minWidth: 0,
      },
      preTradeClearButton: {
        minWidth: 0,
        padding: "4px 6px",
        fontSize: "13px",
        textTransform: "none",
        color: "rgba(224,224,224,0.76)",
        "&:hover": {
          backgroundColor: "rgba(255,255,255,0.08)",
          color: colors.Light,
        },
      },
    });

    const classes = useStyles();

    // Pre-trade suggestion cards all match the tallest visible card (grid rows
    // are 1fr), never shorter than 2 lines; text beyond 4 lines is clamped with
    // ellipsis. Set inline so MUI's default Button padding can't override it.
    const PRE_TRADE_SUGGESTION_MIN_LINES = 2;
    const PRE_TRADE_SUGGESTION_MAX_LINES = 4;
    const PRE_TRADE_SUGGESTION_LINE_HEIGHT = 1.4;
    const preTradeSuggestionStyle: React.CSSProperties = {
      fontSize: typography.suggestionSize,
      lineHeight: PRE_TRADE_SUGGESTION_LINE_HEIGHT,
      height: "100%",
      minHeight: `calc(${PRE_TRADE_SUGGESTION_MIN_LINES * PRE_TRADE_SUGGESTION_LINE_HEIGHT
        }em + 24px)`,
      padding: "12px 16px",
      alignItems: "center",
    };
    const preTradeSuggestionTextStyle: React.CSSProperties = {
      display: "-webkit-box",
      WebkitLineClamp: PRE_TRADE_SUGGESTION_MAX_LINES,
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
      width: "100%",
    };
    const [messageInput, setMessageInput] = useState("");
    const [messages, setMessages] = useState<ChatMessage[]>(() =>
      isPreTrade ? [] : [{ type: "info", text: defaultMessage }]
    );
    const [loading, setLoading] = useState(false); // Track loading state
    const [searchFlag, setSearchFlag] = useState(false);
    const [regenerateIndex, setRegenerateIndex] = useState(null);
    const [dynamicSuggestions, setDynamicSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(true);
    const [summary, setSummary] = useState("");
    const [summaryVersion, setSummaryVersion] = useState(0);

    // Reference to the container that holds the messages
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const isCancelledRef = useRef(false);

    const [feedbackMessageId, setFeedbackMessageId] = useState<string[]>([]);
    const [feedbackTexts, setFeedbackTexts] = useState<Record<string, string>>(
      {}
    );

    const handleFeedback = (messageId: any, feedback: any, feedbackText: any) => {
      giveFeedback({
        msgId: messageId,
        feedback,
        feedbackText,
        setFeedbackMessageId,
      });
    };
    const clearFeedbackText = (messageId: any) => {
      setFeedbackTexts((prev) => {
        if (!(messageId in prev)) return prev;
        const updated = { ...prev };
        delete updated[messageId];
        return updated;
      });
    };
    const handleFeedbackTextChange = (messageId: any, text: any) => {
      setFeedbackTexts((prev) => ({
        ...prev,
        [messageId]: text,
      }));
    };

    // Scroll to the bottom of the message container whenever messages change
    useEffect(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, [loading]); // Include loading in the dependency array
    const copyToClipboard = (htmlString: any) => {
      // Parse the HTML string to extract plain text
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, "text/html");
      const plainText = doc.body.textContent || "";

      // Copy plain text to clipboard
      navigator.clipboard.writeText(plainText).then(() => {
        alert("Copied to clipboard!");
      });
    };
    const handleSuggestionClick = (suggestion: any) => {
      setMessageInput(suggestion);
      handleSendMessage({ userInput: suggestion });
    };
    const normalizeSummaryContext = (value: string) => {
      let nextValue = value;

      if (normalizeSummaryUtcNoZone) {
        nextValue = normalizeSummaryTextToUtcNoZone(nextValue);
      }

      if (normalizeBestXLabels) {
        nextValue = normalizeBestXLabelsInText(nextValue);
      }

      return nextValue;
    };

    const normalizeVisibleMessage = (value: string) =>
      normalizeBestXLabels ? normalizeBestXLabelsInText(value) : value;

    const handleSendMessage = async ({ userInput }: any) => {
      if (!userInput || !String(userInput).trim()) return;
      setLoading(true);
      isCancelledRef.current = false;
      const currentQuestionCount = messages.filter(
        (msg) => msg.type === "question"
      ).length;
      const shouldRequestSuggestions =
        showSuggestions && currentQuestionCount + 1 < SUGGESTION_CONTEXT_LIMIT;
      try {
        wsRef.current?.close();
      } catch { }
      wsRef.current = null;
      setMessageInput("");
      const newMessage = { type: "question", text: userInput };
      setMessages((prevMessages) => [...prevMessages, newMessage]);
      let lastQAContext = "";
      if (messages.length > 0 && messages[messages.length - 1].type !== "info") {
        lastQAContext = messages
          .filter((msg) => msg.type === "question" || msg.type === "answer")
          .slice(-4)
          .map(
            (msg) =>
              `${msg.type === "question" ? "Question:" : "Answer:"} ${msg.text}`
          )
          .join(" ");
      }

      // Seed the answer with an already-open analysis block so the header and
      // its timer render on send instead of waiting for the first INFO frame.
      // The trailing newline makes server steps append as their own rows.
      setMessages((prev) => [
        ...prev,
        { type: "answer", text: PROCESSING_PLACEHOLDER },
      ]);
      const user_id: any = localStorage.getItem("userId");
      const random_number = Math.floor(100000 + Math.random() * 900000); // Generates a 6-digit random number

      const chat_id = `FXGPT-${user_id}-${random_number}`;
      const requestBody = {
        query: `question: ${newMessage.text}`,
        context: lastQAContext,
        document_id: [""],
        dashboard_name: "",
        countries: [""],
        company_ids: [currencyPair],
        reference_ids: "",
        timestamp: new Date().toISOString(),
        chat_id: chat_id,
        sender: user_id,
        userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        sender_display_name: "USER",
        search_flag: searchFlag,
        origin: "FXGPT",
        show_suggestions: shouldRequestSuggestions,
        summary: normalizeSummaryContext(summary),
        summary_version: summaryVersion,
        user_language: userLanguage,
      };

      try {
        let rafId: number | null = null;
        let streamBuf = "";

        const flushAnswerOncePerFrame = () => {
          if (!streamBuf) return;
          const chunk = streamBuf;
          streamBuf = "";
          setMessages((prev) => {
            if (!prev.length) return prev;
            const updated = [...prev];
            for (let i = updated.length - 1; i >= 0; i--) {
              if (updated[i]?.type === "answer") {
                const prevText = updated[i].text || "";
                updated[i] = { ...updated[i], text: prevText + chunk };
                break;
              }
            }
            return updated;
          });
        };

        const scheduleFrameFlush = () => {
          if (rafId !== null) return;
          rafId = requestAnimationFrame(() => {
            rafId = null;
            flushAnswerOncePerFrame();
          });
        };

        const enqueueText = (s: string) => {
          if (!s) return;
          streamBuf += s;
          scheduleFrameFlush();
        };

        let wsBurstTimer: ReturnType<typeof setTimeout> | null = null;
        let wsBurstBuf = "";

        const addToBurst = (s: string) => {
          if (!s) return;
          wsBurstBuf += s;
          if (wsBurstTimer) return;
          wsBurstTimer = setTimeout(() => {
            enqueueText(wsBurstBuf);
            wsBurstBuf = "";
            wsBurstTimer = null;
          }, 25);
        };

        let reconnectEnabled = true;
        let hasFinished = false;
        let reconnectAttempts = 0;
        const MAX_RECONNECT_ATTEMPTS = 5;
        let lastType: string | null = null;
        // The seeded placeholder already opened one.
        let collapsibleOpen = true;

        const closeCollapsibleIfOpen = () => {
          if (!collapsibleOpen) return;
          collapsibleOpen = false;
          addToBurst("</CollapsibleDiv>");
        };

        // Sources stay as data on the message instead of being spliced into its
        // markup; RenderFromString decides where they land.
        const addSourceUrls = (message: string) => {
          const incoming = String(message)
            .split(" ")
            .filter((url) => url);
          if (!incoming.length) return;

          setMessages((prev) => {
            if (!prev.length) return prev;
            const updated = [...prev];
            for (let i = updated.length - 1; i >= 0; i--) {
              if (updated[i]?.type === "answer") {
                const merged = [...(updated[i].sources || [])];
                incoming.forEach((url) => {
                  if (!merged.includes(url)) merged.push(url);
                });
                updated[i] = { ...updated[i], sources: merged };
                break;
              }
            }
            return updated;
          });
        };

        const stopAll = () => {
          reconnectEnabled = false;
          try {
            wsRef.current?.close();
          } catch { }
          closeCollapsibleIfOpen();
          if (wsBurstTimer) {
            clearTimeout(wsBurstTimer);
            wsBurstTimer = null;
          }
          if (wsBurstBuf) {
            enqueueText(wsBurstBuf);
            wsBurstBuf = "";
          }
          flushAnswerOncePerFrame();
          setLoading(false);
          isCancelledRef.current = false;
        };

        const scheduleReconnect = () => {
          if (!reconnectEnabled || hasFinished || isCancelledRef.current) return;
          if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            console.error("Max reconnect attempts reached.");
            stopAll();
            return;
          }
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 10000);
          reconnectAttempts += 1;
          setTimeout(() => {
            void connectWS(true).catch((error) => {
              console.error("WebSocket reconnect auth/setup error:", error);
              setLoading(false);
            });
          }, delay);
        };

        const connectWS = async (_isReconnect = false) => {
          if (!reconnectEnabled || hasFinished || isCancelledRef.current) return;

          const wsUrl = await getAuthenticatedWebSocketUrl(chat_id, user_id);
          const ws = new WebSocket(wsUrl);
          wsRef.current = ws;

          ws.onopen = () => {
            ws.send(JSON.stringify(requestBody));
          };

          ws.onmessage = (event) => {
            if (isCancelledRef.current) {
              stopAll();
              return;
            }

            try {
              const data = JSON.parse(event.data);

              if (data.message) {
                if (data.type === "YAHOO_FINANCE_TICKERS") {
                  return;
                }

                if (data.type === "SUMMARY_UPDATE") {
                  const summaryPayload = parseSummaryPayload(data.message);
                  const nextSummary = summaryPayload?.summary || "";
                  setSummary(normalizeSummaryContext(nextSummary));
                  setSummaryVersion(summaryPayload?.version || 0);
                  return;
                }

                // Types that render nothing inline must not break the open
                // collapsible: "urls" is collected and appended to the last
                // analysis-steps collapsible once the stream is done.
                const isSilentType =
                  data.type === "SUGGESTIONS" ||
                  data.type === "MESSAGE_ID" ||
                  data.type === "urls";

                if (lastType === "CHART" && data.type !== "CHART") {
                  addToBurst("</chartrender>");
                }
                if (collapsibleOpen && data.type !== "INFO" && !isSilentType) {
                  addToBurst("</CollapsibleDiv>");
                  collapsibleOpen = false;
                }
                if (data.type === "INFO" && !collapsibleOpen) {
                  addToBurst("<CollapsibleDiv>");
                  collapsibleOpen = true;
                }
                if (data.type === "CHART" && lastType !== "CHART") {
                  addToBurst("<chartrender>");
                }

                if (data.type === "urls") {
                  addSourceUrls(data.message);
                } else if (data.type === "SUGGESTIONS") {
                  const suggestion_response = JSON.parse(data.message);
                  const nextSuggestions = suggestion_response.suggestions || [];
                  setDynamicSuggestions(
                    normalizeBestXLabels
                      ? nextSuggestions.map((suggestion: string) =>
                        normalizeVisibleMessage(suggestion)
                      )
                      : nextSuggestions
                  );
                } else if (data.type !== "MESSAGE_ID") {
                  const chunk =
                    typeof data.message === "string"
                      ? normalizeVisibleMessage(data.message)
                      : data.message;
                  // Each INFO frame is one analysis step; the newline is what
                  // the renderer splits rows on, so guarantee one per step.
                  addToBurst(
                    data.type === "INFO" &&
                      typeof chunk === "string" &&
                      !/\n\s*$/.test(chunk)
                      ? `${chunk}\n`
                      : chunk
                  );
                }

                lastType = data.type || null;
              }

              if (data.type === "MESSAGE_ID") {
                setMessages((prev) => {
                  const updated = [...prev];
                  for (let i = updated.length - 1; i >= 0; i--) {
                    if (updated[i]?.type === "answer") {
                      updated[i] = { ...updated[i], message_id: data.message };
                      break;
                    }
                  }
                  return updated;
                });
              }

              if (data.type === "FINISH") {
                hasFinished = true;
                reconnectEnabled = false;
                closeCollapsibleIfOpen();
                if (wsBurstTimer) {
                  clearTimeout(wsBurstTimer);
                  wsBurstTimer = null;
                  enqueueText(wsBurstBuf);
                  wsBurstBuf = "";
                }
                flushAnswerOncePerFrame();
                try {
                  ws.close();
                } catch { }
                setLoading(false);
                return;
              }
            } catch (error) {
              console.error("Error parsing WebSocket message:", error);
            }
          };

          ws.onerror = (error) => {
            console.error("WebSocket error:", error);
          };

          ws.onclose = () => {
            if (!hasFinished && !isCancelledRef.current && reconnectEnabled) {
              scheduleReconnect();
            } else {
              isCancelledRef.current = false;
            }
          };
        };

        await connectWS(false);
      } catch (error) {
        console.error("handleSendMessage error:", error);
        setLoading(false);
      }
    };

    useEffect(() => {
      isCancelledRef.current = true;
      try {
        wsRef.current?.close();
      } catch { }
      wsRef.current = null;
      setMessages([]);
      setSummary("");
      setSummaryVersion(0);
      isCancelledRef.current = false;
    }, [currencyPair]);

    useEffect(() => {
      return () => {
        isCancelledRef.current = true;
        try {
          wsRef.current?.close();
        } catch { }
        wsRef.current = null;
      };
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault(); // Prevent default behavior such as form submission
        handleSendMessage({ userInput: messageInput });
      }
    };
    const regenerateAnswer = (index: any, realTime: any) => {
      setSearchFlag(realTime);
      const questionToRegenerate = messages[index - 1]?.text || "";
      setRegenerateIndex(null);
      handleSendMessage({ userInput: questionToRegenerate });
    };
    const toggleRegenerateMenu = (index: any) => {
      setRegenerateIndex(regenerateIndex === index ? null : index);
    };
    const ClearChat = () => {
      isCancelledRef.current = true;
      try {
        wsRef.current?.close();
      } catch { }
      wsRef.current = null;
      setMessages([]);
      setMessageInput("");
      setLoading(false);
      setDynamicSuggestions([]);
      setFeedbackMessageId([]);
      setFeedbackTexts({});
      setSummary("");
      setSummaryVersion(0);
      setRegenerateIndex(null);
    };
    // Only the last answer can still be streaming; earlier ones are settled and
    // keep their action row active throughout.
    const isStreamingMessage = (index: number) =>
      loading && index === messages.length - 1;
    const hasChatActivity = isPreTrade
      ? loading || messages.length > 0
      : loading ||
      dynamicSuggestions.length > 0 ||
      messages.some((msg) => msg.type !== "info");
    const questionMessageCount = messages.filter(
      (msg) => msg.type === "question"
    ).length;
    const canShowDynamicSuggestions =
      questionMessageCount < SUGGESTION_CONTEXT_LIMIT;
    const shouldShowContextNotice =
      !loading && questionMessageCount >= SUGGESTION_CONTEXT_LIMIT;

    return (
      <>
        <div
          className={`${classes.container} ${isPreTrade ? classes.preTradeContainer : ""
            }`}
          ref={containerRef}
          style={{
            padding: isPreTrade
              ? `0 ${typography.containerPadding} ${typography.containerPadding}`
              : typography.containerPadding,
          }}
        >
          <div
            className={`${classes.chatHeader} ${isPreTrade ? classes.preTradeHeader : ""
              }`}
          >
            <span
              className={classes.chatTitle}
              style={{ fontSize: typography.headerSize }}
            >
              {isPreTrade ? "Ask EMAlpha" : "FXGPT"}
            </span>
            {hasChatActivity && (
              isPreTrade ? (
                <Button
                  variant="text"
                  onClick={ClearChat}
                  className={classes.preTradeClearButton}
                >
                  <FaRedo className="me-2" />
                  Clear Chat
                </Button>
              ) : (
                <Button
                  onClick={ClearChat}
                  className="btn btn-danger btn-sm"
                >
                  Reset Chat
                </Button>
              )
            )}
          </div>
          <div
            className={`${classes.container1} ${isPreTrade ? classes.preTradeBody : ""
              }`}
            style={{
              padding: typography.bodyPadding,
              // Set after the shorthand so the last line clears the composer.
              paddingBottom: "28px",
              gap: isPreTrade ? typography.bodyGap : undefined,
            }}
          >
            {messages.length === 0 && showSuggestions && (
              <div
                className={isPreTrade ? "w-100" : "mb-3 w-100"}
                id="suggestion_section"
              >
                <div
                  className={
                    isPreTrade ? classes.preTradeSuggestionGrid : "row"
                  }
                >
                  {chatSuggestions.map((suggestion, index) => (
                    <div
                      className={isPreTrade ? "" : "col-6 mb-1"}
                      key={index}
                    >
                      <Button
                        className={
                          isPreTrade
                            ? classes.preTradeSuggestionButton
                            : "btn btn-light suggestion-button w-100 text-light-gray"
                        }
                        onClick={() => handleSuggestionClick(suggestion)}
                        title={isPreTrade ? suggestion : undefined}
                        style={{
                          fontSize: typography.suggestionSize,
                          ...(isPreTrade
                            ? preTradeSuggestionStyle
                            : {
                              maxWidth: "100%",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "normal",
                              lineHeight: "1.3em",
                              height: typography.suggestionHeight,
                              color: "text-dark-gray",
                            }),
                        }}
                      >
                        {isPreTrade ? (
                          <span style={preTradeSuggestionTextStyle}>
                            {suggestion}
                          </span>
                        ) : (
                          suggestion
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, index) =>
              // An answer with no text at all renders nothing; returning null
              // keeps indices aligned for edit/regenerate.
              msg.type === "answer" && !String(msg.text || "").trim() ? null : (
                <div
                  key={index}
                  className={`${classes.message} ${msg.type === "question" ? classes.question : classes.answer
                    }`}
                  style={{
                    fontSize: typography.messageSize,
                    lineHeight: typography.messageLineHeight,
                    // Bubble padding belongs to questions; answers sit flush.
                    padding:
                      msg.type === "question" ? typography.messagePadding : 0,
                    margin: isPreTrade ? 0 : undefined,
                  }}
                >
                  <RenderFromString htmlString={msg.text} sources={msg.sources} />
                  {msg.type === "answer" && (
                    <>
                      {/* The row stays mounted and just fades in, so finishing a
                          stream never shifts layout, and earlier answers keep
                          their actions while a new one streams. */}
                      <div
                        className={classes.actionRow}
                        style={{
                          opacity: isStreamingMessage(index) ? 0 : 1,
                          pointerEvents: isStreamingMessage(index)
                            ? "none"
                            : "auto",
                        }}
                      >
                        <FaCopy
                          className={classes.actionIcon}
                          onClick={() => copyToClipboard(msg.text)}
                          title="Copy Message"
                        />
                        {msg.message_id &&
                          !feedbackMessageId.includes(msg.message_id) && (
                            <>
                              <FaThumbsUp
                                className={classes.actionIcon}
                                title="Good Response"
                                onClick={() => {
                                  clearFeedbackText(msg.message_id);
                                  handleFeedback(
                                    msg.message_id,
                                    "Positive",
                                    "Positive"
                                  );
                                }}
                              />
                              <FaThumbsDown
                                className={classes.actionIcon}
                                title="Bad Response"
                                onClick={() =>
                                  setFeedbackTexts((prev) => ({
                                    ...prev,
                                    [msg.message_id]: "",
                                  }))
                                }
                              />
                            </>
                          )}
                        <FaRedo
                          className={classes.actionIcon}
                          title="Regenerate Answer"
                          onClick={() => regenerateAnswer(index, searchFlag)}
                        />
                        <FaPen
                          className={classes.actionIcon}
                          title="Edit Message"
                          onClick={() => {
                            setMessageInput(messages[index - 1].text);
                            const inputEl =
                              document.getElementById("messageInput");
                            if (inputEl) {
                              inputEl.focus();
                            }
                          }}
                        />
                      </div>
                    </>
                  )}
                  {msg.message_id &&
                    feedbackTexts[msg.message_id] !== undefined &&
                    !feedbackMessageId.includes(msg.message_id) && (
                      <div className={classes.feedbackBox}>
                        <label
                          className={classes.feedbackLabel}
                          htmlFor={`feedback-${msg.message_id}`}
                        >
                          What went wrong?
                        </label>
                        <textarea
                          id={`feedback-${msg.message_id}`}
                          className={classes.feedbackInput}
                          rows={2}
                          autoFocus
                          placeholder="Optional: share details to help us improve"
                          value={feedbackTexts[msg.message_id]}
                          onChange={(e) =>
                            handleFeedbackTextChange(msg.message_id, e.target.value)
                          }
                        />
                        <div className={classes.feedbackActions}>
                          <Button
                            variant="text"
                            className={`${classes.feedbackButton} ${classes.feedbackCancelButton}`}
                            onClick={() => clearFeedbackText(msg.message_id)}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            className={classes.feedbackButton}
                            onClick={() => {
                              handleFeedback(
                                msg.message_id,
                                "Negative",
                                feedbackTexts[msg.message_id]
                              );
                              clearFeedbackText(msg.message_id);
                            }}
                          >
                            Submit
                          </Button>
                        </div>
                      </div>
                    )}
                </div>
              )
            )}

            {dynamicSuggestions.length > 0 && !loading && canShowDynamicSuggestions && (
              <div
                className={isPreTrade ? "w-100" : "mb-3 w-100"}
                id="suggestion_section"
                style={{ transition: "all 0.8s" }}
              >
                <div
                  className={
                    isPreTrade ? classes.preTradeSuggestionGrid : "row"
                  }
                >
                  {dynamicSuggestions.map((suggestion, index) => (
                    <div
                      className={isPreTrade ? "" : "col-6 mb-1"}
                      key={index}
                    >
                      <Button
                        className={
                          isPreTrade
                            ? classes.preTradeSuggestionButton
                            : "btn btn-light suggestion-button w-100 text-light-gray"
                        }
                        onClick={() => handleSuggestionClick(suggestion)}
                        title={isPreTrade ? suggestion : undefined}
                        style={{
                          fontSize: typography.suggestionSize,
                          ...(isPreTrade
                            ? preTradeSuggestionStyle
                            : {
                              maxWidth: "100%",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "normal",
                              lineHeight: "1.3em",
                              height: typography.suggestionHeight,
                              color: "text-dark-gray",
                            }),
                        }}
                      >
                        {isPreTrade ? (
                          <span style={preTradeSuggestionTextStyle}>
                            {suggestion}
                          </span>
                        ) : (
                          suggestion
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!isPreTrade && shouldShowContextNotice && (
              <div
                className={classes.contextNotice}
                style={{
                  fontSize: typography.noticeSize,
                  lineHeight: typography.noticeLineHeight,
                }}
              >
                {SUGGESTION_CONTEXT_NOTICE}
              </div>
            )}
            {/* Progress now lives in the analysis-steps header, which shows the
                live step and an elapsed timer while the answer streams. */}
            {/* The element to scroll to */}
            <div ref={messagesEndRef} />
          </div>

          {!isPreTrade && (
            <ChatFinePrint
              assistantName="FXGPT"
              style={{
                marginTop: "6px",
                fontSize: typography.finePrintSize,
                lineHeight: typography.finePrintLineHeight,
              }}
            />
          )}

          <div
            className={isPreTrade ? classes.preTradeComposer : ""}
            style={
              isPreTrade
                ? undefined
                : {
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  marginTop: "10px",
                }
            }
          >
            {/* <div className="d-flex  flex-column">
            <span className="me-1 " style={{ fontSize: "16px", width: "75px" }}>
              Real-time:
            </span>
          </div>
          <Switch
            onChange={() => setSearchFlag(!searchFlag)}
            checked={searchFlag}
            height={20}
            width={40}
            className="me-2 "
          /> */}

            <div className={isPreTrade ? classes.preTradeInputWrap : "w-100"}>
              <TextField
                size={isPreTrade ? "small" : undefined}
                placeholder={isPreTrade ? "Type your question" : enterMessage}
                value={messageInput}
                id="messageInput"
                onChange={(e) => setMessageInput(e)}
                onKeyDown={handleKeyDown}
                endAdornment={
                  <div
                    className=" hover:text-info"
                    onClick={() =>
                      handleSendMessage({ userInput: messageInput })
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <i className="fa-solid fa-paper-plane me-2  "></i>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      </>
    );
  };

// Source pills wrap inline under a quiet label instead of sitting in their own
// scrolling panel; the surrounding step row already provides the container.
export const SupportingSources: FC<{ urls: string[] }> = ({ urls }) => {
  if (!urls?.length) return null;

  return (
    <div style={{ marginTop: "10px" }}>
      <div
        style={{
          marginBottom: "6px",
          fontSize: "0.8em",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          color: "rgba(232,237,244,0.38)",
        }}
      >
        Sources
      </div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
        }}
      >
        {urls.map((url) => (
          <LinkPreview key={url} url={url} />
        ))}
      </div>
    </div>
  );
};

// Last collapsible in document order, however deeply it is nested.
const findLastCollapsible = (nodes: any[]): any => {
  let last: any = null;
  const walk = (list: any[]) => {
    (list || []).forEach((node) => {
      if (node?.name === "collapsiblediv") last = node;
      if (node?.children?.length) walk(node.children);
    });
  };
  walk(nodes);
  return last;
};

// INFO messages stream in back to back, so one collapsible holds every step as
// a flat run of nodes. Line breaks are the only separator the backend gives us;
// split on them so each step can render as its own row.
const splitIntoSteps = (children: any[]): any[][] => {
  const steps: any[][] = [];
  let current: any[] = [];

  const endStep = () => {
    if (current.length) steps.push(current);
    current = [];
  };

  (children || []).forEach((node) => {
    if (node?.type === "text") {
      const pieces = String(node.data || "").split(/\r?\n/);
      pieces.forEach((piece, index) => {
        if (index > 0) endStep();
        // Plain strings render as-is; only real nodes need domToReact.
        if (piece.trim()) current.push(piece);
      });
      return;
    }
    if (node?.name === "br") {
      endStep();
      return;
    }
    current.push(node);
  });
  endStep();

  return steps;
};

export const RenderFromString: any = ({ htmlString, sources }: any) => {
  if (typeof htmlString !== "string") {
    console.error(
      "RenderFromString expects a string but received:",
      htmlString
    );
    return null;
  }

  const sourceUrls: string[] = Array.isArray(sources) ? sources : [];
  const dom = htmlToDOM(htmlString) as any[];
  // Supporting sources hang off the bottom of the last analysis steps
  // collapsible; with no collapsible to hold them they follow the answer.
  const lastCollapsible = sourceUrls.length ? findLastCollapsible(dom) : null;
  // An unclosed trailing <CollapsibleDiv> means steps are still streaming, so
  // the header shows the live step instead of the finished summary.
  const lowered = htmlString.toLowerCase();
  const openCollapsible =
    lowered.lastIndexOf("<collapsiblediv>") >
      lowered.lastIndexOf("</collapsiblediv>")
      ? findLastCollapsible(dom)
      : null;

  const parseOptions: any = {
    replace: (domNode: any) => {
      if (domNode.name === "linkpreview") {
        const url = domNode.attribs?.url || "#";
        return <LinkPreview url={url} />;
      }
      if (domNode.name === "collapsiblediv") {
        // Pass the options down so link previews nested inside the analysis
        // steps collapsible are still replaced with real components.
        const steps = splitIntoSteps(domNode.children).map((step, index) => (
          <React.Fragment key={index}>
            {step.map((item, itemIndex) =>
              typeof item === "string" ? (
                item
              ) : (
                <React.Fragment key={itemIndex}>
                  {domToReact([item], parseOptions)}
                </React.Fragment>
              )
            )}
          </React.Fragment>
        ));

        return (
          <CollapsibleDiv
            steps={steps}
            active={domNode === openCollapsible}
            footer={
              domNode === lastCollapsible ? (
                <SupportingSources urls={sourceUrls} />
              ) : null
            }
          />
        );
      }
      if (domNode.name === "chartrender") {
        return (
          <HighchartFromText chartText={domNode.childNodes[0]?.data || ""} />
        );
      }
    },
  };

  return (
    <>
      {domToReact(dom as any, parseOptions)}
      {sourceUrls.length && !lastCollapsible ? (
        <SupportingSources urls={sourceUrls} />
      ) : null}
    </>
  );
};
export default CoTraderChatTileComponent;
